// Start Here is where an idea is born, so it opens empty.
//
// The first version of this screen picked the most recent project and showed
// its thread. That is a workbench, not a blank page: a creator who arrives
// with a new idea has to get somebody else's work off the table first, and
// what they wrote was read as a note on last week's episode.
//
// So: nothing is selected, the thread is empty, and the first thing typed
// STARTS something. Everything after it — the title, the scenes, the cast, the
// look — follows from that one sentence. Work already under way is reached
// from the board, or from one line here that has to be asked for.

/** As long a title as the card column and the board will carry well. */
export const MAX_TITLE = 70;

/** Openers people type without meaning them as part of the title. */
const OPENERS = [
  // Each ends at whitespace OR the end of the string, so an opener typed with
  // nothing after it is peeled away too — otherwise "a video about" becomes an
  // episode called "A video about", which is worse than no title at all.
  /^(ok(ay)?|so|right|hey)[,\s]+/i,
  /^(i(?: would| 'd| wanna| want to| would like to)?\s+)?(want|need|like)\s+(to\s+)?(make|build|do|create)(?:\s+|$)/i,
  /^(let'?s|lets)\s+(make|build|do|create|try)(?:\s+|$)/i,
  /^(make|build|create|do)(?:\s+|$)/i,
  /^(a|an|the)\s+(video|episode|short|film|series|lab)\s+(about|on|for)(?:\s+|$)/i,
  /^(a|an|the)\s+(show|piece)\s+(about|on)(?:\s+|$)/i,
  /^(video|episode|show)\s+(about|on)(?:\s+|$)/i,
  /^(about|on)(?:\s+|$)/i,
];

/**
 * The title an idea starts life with.
 *
 * Derived rather than asked for, because asking "what is it called?" before
 * the idea is out is the wrong question at the wrong moment — a creator who
 * knows the title already has an outline, and this screen is for the ones who
 * do not. It can be renamed on the board in a second.
 *
 * The rules are conservative: strip what people say before the subject, take
 * the first sentence, cut on a word boundary, capitalise. Never invent.
 */
export function titleFrom(idea: string): string {
  let text = idea.replace(/\s+/g, ' ').trim();
  if (!text) return 'New episode';

  // Peel openers one at a time, so "let's make a video about dragons" loses
  // both halves and keeps "dragons".
  for (let i = 0; i < 4; i += 1) {
    const before = text;
    for (const re of OPENERS) text = text.replace(re, '');
    if (text === before) break;
  }
  text = text.trim();
  if (!text) return 'New episode';

  // One sentence. A paragraph of premise is the brief, not the name.
  const stop = text.search(/[.!?](\s|$)/);
  if (stop > 8) text = text.slice(0, stop);

  if (text.length > MAX_TITLE) {
    const cut = text.slice(0, MAX_TITLE);
    const space = cut.lastIndexOf(' ');
    text = (space > 24 ? cut.slice(0, space) : cut).replace(/[,;:\s]+$/, '');
  }
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Enough of an idea to start something, as opposed to a stray keystroke. */
export const MIN_IDEA = 6;

/**
 * As much as the story editor reads: a whole production brief, not a tweet.
 * The same number as MAX_IDEA in the API's storyline.ts; the route refuses
 * past it, so the box stops there too rather than failing on send.
 */
export const MAX_IDEA = 6000;

export function isEnough(idea: string): boolean {
  return idea.trim().length >= MIN_IDEA;
}
